import { CredentialType } from "@api/shared-runtime/enums";
import type { RouterInputs } from "@/trpc";
import type { Credential } from "@/features/credentials/types";
import { asDetails } from "../credential-card/credential-details/detail-values";
import {
  formatStringList,
  parseStringList,
  type CredentialFormValues,
} from "../credential-edit-modal/credential-form-values";
import { getProviderSpecialtyCodes } from "@/features/credentials/lib/provider-specialties";
import type { ProfessionalDetailsValue } from "./provider-professional-details-form";

type RelatedProfessionalProfile = NonNullable<
  RouterInputs["credentials"]["createMyCredential"]["relatedProfessionalProfile"]
>;

const PROFESSIONAL_FIELDS = [
  "providerType",
  "providerRole",
  "specialtyCodes",
  "practiceSetting",
  "primaryPracticeState",
  "additionalPracticeStates",
  "languages",
] as const;
const PROFESSIONAL_LIST_FIELDS = new Set<string>([
  "specialtyCodes",
  "additionalPracticeStates",
  "languages",
]);
const NAME_FIELDS = ["first", "middle", "last", "suffix"] as const;
const NPI_PATTERN = /^\d{10}$/;

function professionalKey(type: CredentialType, field: string): string {
  const prefix = type === CredentialType.PersonalInfo ? "details" : "related";
  return `${prefix}.professional.${field}`;
}

function nameKey(type: CredentialType, field: string): string {
  const prefix = type === CredentialType.PersonalInfo ? "details" : "related";
  return `${prefix}.name.${field}`;
}

function npiKeys(type: CredentialType) {
  if (type === CredentialType.Npi) {
    return { number: "column.number", taxonomyCodes: "details.taxonomyCodes" };
  }
  return {
    number: "related.npi.number",
    taxonomyCodes: "related.npi.taxonomyCodes",
  };
}

export function hasProfessionalProfile(type: CredentialType): boolean {
  return type === CredentialType.PersonalInfo || type === CredentialType.Npi;
}

/** Related drafts use separate keys so they never leak into the main substance.
 * The edited record takes precedence over its possibly older list snapshot. */
export function initialProfessionalValues({
  credential,
  credentials,
  values,
}: {
  credential: Credential;
  credentials: Credential[];
  values: CredentialFormValues;
}): CredentialFormValues {
  const type = credential.credentialType;
  if (!hasProfessionalProfile(type)) {
    return values;
  }
  const records = [
    credential,
    ...credentials.filter((entry) => entry.credentialType !== type),
  ];
  const personal = records.find(
    (entry) =>
      entry.present && entry.credentialType === CredentialType.PersonalInfo,
  );
  const npi = records.find(
    (entry) => entry.present && entry.credentialType === CredentialType.Npi,
  );
  const details = asDetails(personal?.version?.details);
  const professional = asDetails(details.professional);
  const keys = npiKeys(type);
  const result = { ...values };
  if (type === CredentialType.PersonalInfo) {
    result[keys.number] = npi?.version?.number ?? "";
    result[keys.taxonomyCodes] = formatStringList(
      asDetails(npi?.version?.details).taxonomyCodes,
    );
  } else {
    for (const field of PROFESSIONAL_FIELDS) {
      const raw = professional[field];
      result[professionalKey(type, field)] = PROFESSIONAL_LIST_FIELDS.has(field)
        ? formatStringList(raw)
        : String(raw ?? "");
    }
    for (const field of NAME_FIELDS) {
      result[nameKey(type, field)] = String(
        asDetails(details.name)[field] ?? "",
      );
    }
  }
  result[professionalKey(type, "specialtyCodes")] = formatStringList(
    getProviderSpecialtyCodes(records),
  );
  return result;
}

export function professionalDetailsValue({
  credentialType,
  values,
}: {
  credentialType: CredentialType;
  values: CredentialFormValues;
}): ProfessionalDetailsValue {
  const text = (field: string) =>
    String(values[professionalKey(credentialType, field)] ?? "");
  return {
    npiNumber: String(values[npiKeys(credentialType).number] ?? ""),
    providerType: text("providerType"),
    providerRole: text("providerRole"),
    specialtyCodes: parseStringList(text("specialtyCodes")),
    practiceSetting: text("practiceSetting"),
    primaryPracticeState: text("primaryPracticeState"),
    additionalPracticeStates: parseStringList(text("additionalPracticeStates")),
    languages: parseStringList(text("languages")),
  };
}

export function professionalDetailsPatch({
  credentialType,
  patch,
}: {
  credentialType: CredentialType;
  patch: Partial<ProfessionalDetailsValue>;
}): CredentialFormValues {
  const result: CredentialFormValues = {};
  for (const [field, value] of Object.entries(patch)) {
    const key =
      field === "npiNumber"
        ? npiKeys(credentialType).number
        : professionalKey(credentialType, field);
    result[key] = Array.isArray(value)
      ? formatStringList(value)
      : String(value ?? "");
  }
  return result;
}

export function npiSelectionPatch({
  credentialType,
  selection,
}: {
  credentialType: CredentialType;
  selection: {
    npiNumber: string;
    taxonomyCodes: string[];
    providerType: string;
    providerRole: string;
    name: { first: string; middle?: string; last: string };
  };
}): CredentialFormValues {
  return {
    ...professionalDetailsPatch({
      credentialType,
      patch: {
        npiNumber: selection.npiNumber,
        providerType: selection.providerType,
        providerRole: selection.providerRole,
        specialtyCodes: selection.taxonomyCodes,
      },
    }),
    [npiKeys(credentialType).taxonomyCodes]: formatStringList(
      selection.taxonomyCodes,
    ),
    [nameKey(credentialType, "first")]: selection.name.first,
    [nameKey(credentialType, "middle")]: selection.name.middle ?? "",
    [nameKey(credentialType, "last")]: selection.name.last,
  };
}

/** Send only deliberate changes to the sibling record. The server merges them
 * into its latest holding so unrelated edits and newly attached evidence survive. */
export function relatedProfessionalProfile({
  credentialType,
  values,
  baseline,
}: {
  credentialType: CredentialType;
  values: CredentialFormValues;
  baseline: CredentialFormValues;
}): RelatedProfessionalProfile | undefined {
  if (credentialType === CredentialType.PersonalInfo) {
    const keys = npiKeys(credentialType);
    if (
      values[keys.number] === baseline[keys.number] &&
      values[keys.taxonomyCodes] === baseline[keys.taxonomyCodes]
    ) {
      return;
    }
    const number = String(values[keys.number] ?? "").trim() || null;
    const npi: NonNullable<RelatedProfessionalProfile["npi"]> = { number };
    if (number && values[keys.taxonomyCodes] !== baseline[keys.taxonomyCodes]) {
      npi.taxonomyCodes = parseStringList(
        String(values[keys.taxonomyCodes] ?? ""),
      );
    }
    return { npi };
  }
  if (credentialType !== CredentialType.Npi) {
    return;
  }
  const professional: NonNullable<RelatedProfessionalProfile["professional"]> =
    {};
  for (const field of PROFESSIONAL_FIELDS) {
    const key = professionalKey(credentialType, field);
    if (values[key] === baseline[key]) {
      continue;
    }
    const text = String(values[key] ?? "").trim();
    Object.assign(professional, {
      [field]: PROFESSIONAL_LIST_FIELDS.has(field)
        ? parseStringList(text)
        : text || null,
    });
  }
  const changedName = NAME_FIELDS.some(
    (field) =>
      values[nameKey(credentialType, field)] !==
      baseline[nameKey(credentialType, field)],
  );
  const result: RelatedProfessionalProfile = {};
  if (Object.keys(professional).length > 0) {
    result.professional = professional;
  }
  if (changedName) {
    result.name = {
      first: String(values[nameKey(credentialType, "first")] ?? "").trim(),
      middle: String(values[nameKey(credentialType, "middle")] ?? "").trim(),
      last: String(values[nameKey(credentialType, "last")] ?? "").trim(),
    };
  }
  return Object.keys(result).length > 0 ? result : undefined;
}

export function validateProfessionalDetails({
  credentialType,
  values,
}: {
  credentialType: CredentialType;
  values: CredentialFormValues;
}): string | null {
  if (!hasProfessionalProfile(credentialType)) {
    return null;
  }
  const npi = String(values[npiKeys(credentialType).number] ?? "").trim();
  if (
    (npi || credentialType === CredentialType.Npi) &&
    !NPI_PATTERN.test(npi)
  ) {
    return "Enter a 10-digit NPI or select your record from the NPI search.";
  }
  return null;
}
